// ============================================================
//  src/pages/OrdersPage.jsx
//  Updated: Material Icons Round + DM Sans / Playfair Display
//  (fonts & icons loaded globally via App.jsx useGlobalAssets)
// ============================================================

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Toast } from "../components/UI";
import { supabase } from "../utils/supabase";
import config from "../config";

// ── Material Icon helper ──────────────────────────────────────
function Icon({ name, style = {} }) {
  return (
    <span
      className="material-icons-round"
      style={{ fontSize: 20, verticalAlign: "middle", lineHeight: 1, ...style }}
    >
      {name}
    </span>
  );
}

// ── Status config ─────────────────────────────────────────────
const STATUS_MAP = {
  pending:   { bg: "#fff7ed", color: "#c2410c", icon: "hourglass_top",   label: "Pending",   desc: "We've received your order and are verifying payment." },
  confirmed: { bg: "#f0fdf4", color: "#15803d", icon: "check_circle",    label: "Confirmed", desc: "Payment confirmed! We're preparing your order."         },
  shipped:   { bg: "#eff6ff", color: "#1d4ed8", icon: "local_shipping",  label: "Shipped",   desc: "Your order is on the way!"                             },
  delivered: { bg: "#f5f3ff", color: "#6d28d9", icon: "inventory_2",     label: "Delivered", desc: "Order delivered. Enjoy your products!"                 },
  cancelled: { bg: "#fef2f2", color: "#dc2626", icon: "cancel",          label: "Cancelled", desc: "This order was cancelled."                              },
};
function getStatus(key) { return STATUS_MAP[key] || STATUS_MAP.pending; }

function fmtDate(iso) {
  if (!iso) return "Date not recorded";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Invalid date";
  return d.toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── Status badge ──────────────────────────────────────────────
function StatusBadge({ statusKey }) {
  const s = getStatus(statusKey);
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 800,
      padding: "4px 10px", borderRadius: 20,
      whiteSpace: "nowrap", display: "inline-flex",
      alignItems: "center", gap: 4,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <Icon name={s.icon} style={{ fontSize: 13, color: s.color }} />
      {s.label}
    </span>
  );
}

// ── Order progress tracker ────────────────────────────────────
function StatusTracker({ statusKey }) {
  if (statusKey === "cancelled") return null;
  const steps   = ["pending", "confirmed", "shipped", "delivered"];
  const current = steps.indexOf(statusKey);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        {steps.map((step, i) => {
          const s      = getStatus(step);
          const done   = i <= current;
          const active = i === current;
          return (
            <div key={step} style={{
              flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", position: "relative",
            }}>
              {i > 0 && (
                <div style={{
                  position: "absolute", top: 12, right: "50%",
                  width: "100%", height: 2, zIndex: 0,
                  background: i <= current ? config.colors.primary : "#e4e2dc",
                }} />
              )}
              <div style={{
                width: 26, height: 26, borderRadius: "50%", zIndex: 1,
                background: done ? config.colors.primary : "#f0ede8",
                border: active ? `3px solid ${config.colors.primary}` : "2px solid transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <Icon
                  name={done ? s.icon : "radio_button_unchecked"}
                  style={{ fontSize: 14, color: done ? "#fff" : "#ccc" }}
                />
              </div>
              <div style={{
                fontSize: 9, marginTop: 4, textAlign: "center", lineHeight: 1.3,
                fontWeight: active ? 800 : 600,
                color: active ? config.colors.primary : done ? "#555" : "#ccc",
                fontFamily: "'DM Sans', sans-serif",
              }}>
                {s.label}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{
        marginTop: 12, background: getStatus(statusKey).bg,
        borderRadius: 10, padding: "8px 12px", fontSize: 12,
        color: getStatus(statusKey).color, fontWeight: 600,
        display: "flex", alignItems: "center", gap: 6,
        fontFamily: "'DM Sans', sans-serif",
      }}>
        <Icon name={getStatus(statusKey).icon} style={{ fontSize: 14, color: getStatus(statusKey).color }} />
        {getStatus(statusKey).desc}
      </div>
    </div>
  );
}

// ── Single order card ─────────────────────────────────────────
function OrderCard({ order, onWhatsApp }) {
  const [expanded, setExpanded] = useState(false);
  const items     = order.items   || [];
  const address   = order.address || {};
  const statusKey = order.status  || "pending";

  return (
    <Card style={{ marginBottom: 14 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{
            fontSize: 11, color: "#bbb", fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 0.4,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            Order ID
          </div>
          <div style={{ fontSize: 13, fontWeight: 800, color: config.colors.text, fontFamily: "'DM Sans', sans-serif" }}>
            #{String(order.id).padStart(5, "0")}
          </div>
        </div>
        <StatusBadge statusKey={statusKey} />
      </div>

      <StatusTracker statusKey={statusKey} />

      {/* Meta row */}
      <div style={{ display: "flex", gap: 20, marginBottom: 12 }}>
        {[
          { label: "Date",  value: fmtDate(order.created_at), icon: "calendar_today" },
          { label: "Total", value: `₹${order.total}`,         icon: "payments",       bold: true },
          { label: "Items", value: `${items.length} item${items.length !== 1 ? "s" : ""}`, icon: "inventory_2" },
        ].map(({ label, value, icon, bold }) => (
          <div key={label}>
            <div style={{
              fontSize: 10, color: "#bbb", fontWeight: 700,
              textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif",
              display: "flex", alignItems: "center", gap: 3, marginBottom: 2,
            }}>
              <Icon name={icon} style={{ fontSize: 10, color: "#bbb" }} />
              {label}
            </div>
            <div style={{
              fontSize: bold ? 14 : 12, fontWeight: bold ? 900 : 600,
              color: bold ? config.colors.primary : config.colors.text,
              fontFamily: "'DM Sans', sans-serif",
            }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Items list */}
      <div style={{ background: "#faf8f5", borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
        {items.slice(0, expanded ? items.length : 2).map((item, i) => (
          <div key={i} style={{
            display: "flex", justifyContent: "space-between", fontSize: 13,
            paddingBottom: i < items.length - 1 ? 6 : 0,
            marginBottom:  i < items.length - 1 ? 6 : 0,
            borderBottom:  i < items.length - 1 ? `1px solid ${config.colors.cardBorder}` : "none",
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <span style={{ color: "#555", flex: 1, marginRight: 8 }}>
              {item.name} {item.size ? `(${item.size})` : ""} × {item.qty}
            </span>
            <span style={{ fontWeight: 800, color: config.colors.primary, flexShrink: 0 }}>
              ₹{item.subtotal}
            </span>
          </div>
        ))}
        {items.length > 2 && (
          <div
            onClick={() => setExpanded(!expanded)}
            style={{
              fontSize: 12, color: config.colors.accent, fontWeight: 700,
              marginTop: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <Icon
              name={expanded ? "expand_less" : "expand_more"}
              style={{ fontSize: 16, color: config.colors.accent }}
            />
            {expanded ? "Show less" : `+${items.length - 2} more items`}
          </div>
        )}
      </div>

      {/* Delivery address */}
      {(address.line1 || address.city) && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: 10, color: "#bbb", fontWeight: 700,
            textTransform: "uppercase", marginBottom: 4,
            display: "flex", alignItems: "center", gap: 4,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <Icon name="location_on" style={{ fontSize: 12, color: "#bbb" }} />
            Delivery Address
          </div>
          <div style={{ fontSize: 12, color: "#666", lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>
            {[address.line1, address.line2, address.city, address.state, address.pincode].filter(Boolean).join(", ")}
          </div>
        </div>
      )}

      {/* WhatsApp follow-up button */}
      <button
        onClick={() => onWhatsApp(order)}
        style={{
          width: "100%", background: "#25D366", color: "#fff",
          border: "none", borderRadius: 10, padding: "10px 0",
          fontWeight: 800, fontSize: 13, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <Icon name="chat" style={{ fontSize: 17, color: "#fff" }} />
        Follow Up on WhatsApp
      </button>
    </Card>
  );
}

// ── Skeleton loader ───────────────────────────────────────────
function OrderSkeleton() {
  const shimmer = {
    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.4s infinite",
    borderRadius: 8,
  };
  return (
    <>
      <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{
          background: "#fff", borderRadius: 16, padding: "18px 16px",
          marginBottom: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
          border: "1px solid #f0ede8",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ ...shimmer, width: 80, height: 14 }} />
            <div style={{ ...shimmer, width: 70, height: 22, borderRadius: 20 }} />
          </div>
          <div style={{ ...shimmer, width: "100%", height: 48, borderRadius: 10, marginBottom: 14 }} />
          <div style={{ display: "flex", gap: 20, marginBottom: 14 }}>
            <div style={{ ...shimmer, width: 60, height: 12 }} />
            <div style={{ ...shimmer, width: 50, height: 12 }} />
          </div>
          <div style={{ ...shimmer, width: "100%", height: 64, borderRadius: 10, marginBottom: 10 }} />
          <div style={{ ...shimmer, width: "100%", height: 40, borderRadius: 10 }} />
        </div>
      ))}
    </>
  );
}

// ── Empty state ───────────────────────────────────────────────
function EmptyOrders() {
  const navigate = useNavigate();
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <Icon name="receipt_long" style={{ fontSize: 64, color: config.colors.primary, marginBottom: 16, display: "block" }} />
      <div style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 20, fontWeight: 800,
        color: config.colors.primary, marginBottom: 8,
      }}>
        No Orders Yet
      </div>
      <div style={{ fontSize: 14, color: "#aaa", marginBottom: 24, fontFamily: "'DM Sans', sans-serif" }}>
        Your order history will appear here once you place your first order.
      </div>
      <button
        onClick={() => navigate("/")}
        style={{
          background: config.colors.primary, color: "#fff",
          border: "none", borderRadius: 13, padding: "13px 28px",
          fontWeight: 800, fontSize: 14, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 8,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <Icon name="shopping_cart" style={{ fontSize: 18, color: "#fff" }} />
        Start Shopping
      </button>
    </div>
  );
}

// ── Main OrdersPage ───────────────────────────────────────────
export default function OrdersPage({ auth }) {
  const navigate              = useNavigate();
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);

  useEffect(() => {
    if (!auth.user) return;
    let cancelled = false;

    supabase
      .from("orders_table")
      .select("id, created_at, total, items, address, status")
      .eq("user_id", auth.user.id)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error("Fetch orders error:", error);
        else setOrders(data || []);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    const channel = supabase
      .channel(`orders_user_${auth.user.id}`)
      .on("postgres_changes", {
        event: "UPDATE", schema: "public",
        table: "orders_table", filter: `user_id=eq.${auth.user.id}`,
      }, (payload) => {
        if (cancelled) return;
        setOrders((prev) =>
          prev.map((o) => o.id === payload.new.id ? { ...o, status: payload.new.status } : o)
        );
      })
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, [auth.user]);

  function handleWhatsApp(order) {
    const items      = (order.items || []).map((i) => `• ${i.name} (${i.size || "-"}) × ${i.qty} = ₹${i.subtotal}`).join("\n");
    const address    = order.address || {};
    const addrStr    = [address.line1, address.line2, address.city, address.state, address.pincode].filter(Boolean).join(", ");
    const statusInfo = getStatus(order.status || "pending");
    const msg = encodeURIComponent(
      `Hi RK Farm Foods! 👋\n\nI'd like to follow up on my order:\n\n` +
      `🧾 Order ID: #${String(order.id).padStart(5, "0")}\n📅 Date: ${fmtDate(order.created_at)}\n📌 Status: ${statusInfo.label}\n\n📦 Items:\n${items}\n\n💰 Total: ₹${order.total}\n\n📍 Delivery Address:\n${addrStr}\n\nPlease update me on the status. Thank you!`
    );
    window.open(`https://wa.me/${config.whatsappNumber || ""}?text=${msg}`, "_blank");
  }

  useEffect(() => {
    if (!auth.user) navigate("/signin");
  }, [auth.user]); // eslint-disable-line

  if (!auth.user) return null;

  return (
    <div style={{ padding: "18px 13px 100px", maxWidth: 520, margin: "0 auto" }}>

      {/* ── Hero header ── */}
      <div style={{
        background: `linear-gradient(135deg, ${config.colors.primary}, ${config.colors.primaryLight})`,
        borderRadius: 20, padding: "22px 20px", color: "#fff", marginBottom: 18,
      }}>
        <Icon name="receipt_long" style={{ fontSize: 36, color: "#fff", marginBottom: 8, display: "block" }} />
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800 }}>
          My Orders
        </div>
        <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>
          {loading ? "Loading..." : `${orders.length} order${orders.length !== 1 ? "s" : ""} placed`}
        </div>
      </div>

      {loading  && <OrderSkeleton />}
      {!loading && orders.length === 0 && <EmptyOrders />}
      {!loading && orders.map((order) => (
        <OrderCard key={order.id} order={order} onWhatsApp={handleWhatsApp} />
      ))}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
